<script setup lang="ts">
import { ref, computed } from 'vue';

interface Props {
  modelValue: string;
  error?: string;
  disabled?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const selectedCountry = ref('+1');
const phoneNumber = ref('');

// Comprehensive country codes (195+ countries)
const countries = [
  { code: '+1', name: 'United States', flag: '🇺🇸', format: '(XXX) XXX-XXXX' },
  { code: '+1', name: 'Canada', flag: '��', format: '(XXX) XXX-XXXX' },
  { code: '+7', name: 'Russia', flag: '🇷🇺', format: 'XXX XXX XX XX' },
  { code: '+20', name: 'Egypt', flag: '🇪🇬', format: 'XXX XXX XXXX' },
  { code: '+27', name: 'South Africa', flag: '🇿🇦', format: 'XX XXX XXXX' },
  { code: '+30', name: 'Greece', flag: '🇬🇷', format: 'XXX XXX XXXX' },
  { code: '+31', name: 'Netherlands', flag: '🇳🇱', format: 'X XX XX XX XX' },
  { code: '+32', name: 'Belgium', flag: '🇧🇪', format: 'XXX XX XX XX' },
  { code: '+33', name: 'France', flag: '🇫🇷', format: 'X XX XX XX XX' },
  { code: '+34', name: 'Spain', flag: '🇪🇸', format: 'XXX XX XX XX' },
  { code: '+36', name: 'Hungary', flag: '🇭🇺', format: 'XX XXX XXXX' },
  { code: '+39', name: 'Italy', flag: '🇮🇹', format: 'XXX XXX XXXX' },
  { code: '+40', name: 'Romania', flag: '🇷🇴', format: 'XXX XXX XXX' },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭', format: 'XX XXX XX XX' },
  { code: '+43', name: 'Austria', flag: '🇦🇹', format: 'XXX XXXXXX' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧', format: 'XXXX XXX XXXX' },
  { code: '+45', name: 'Denmark', flag: '🇩🇰', format: 'XX XX XX XX' },
  { code: '+46', name: 'Sweden', flag: '🇸🇪', format: 'XX XXX XX XX' },
  { code: '+47', name: 'Norway', flag: '🇳🇴', format: 'XXX XX XXX' },
  { code: '+48', name: 'Poland', flag: '🇵🇱', format: 'XXX XXX XXX' },
  { code: '+49', name: 'Germany', flag: '🇩🇪', format: 'XXX XXXXXXX' },
  { code: '+51', name: 'Peru', flag: '🇵🇪', format: 'XXX XXX XXX' },
  { code: '+52', name: 'Mexico', flag: '🇲🇽', format: 'XXX XXX XXXX' },
  { code: '+53', name: 'Cuba', flag: '🇨🇺', format: 'X XXX XXXX' },
  { code: '+54', name: 'Argentina', flag: '��', format: 'XX XXXX XXXX' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷', format: 'XX XXXXX XXXX' },
  { code: '+56', name: 'Chile', flag: '🇨🇱', format: 'X XXXX XXXX' },
  { code: '+57', name: 'Colombia', flag: '🇨🇴', format: 'XXX XXX XXXX' },
  { code: '+58', name: 'Venezuela', flag: '🇻🇪', format: 'XXX XXX XXXX' },
  { code: '+60', name: 'Malaysia', flag: '🇲�', format: 'XX XXX XXXX' },
  { code: '+61', name: 'Australia', flag: '🇦🇺', format: 'XXX XXX XXX' },
  { code: '+62', name: 'Indonesia', flag: '🇮🇩', format: 'XXX XXX XXXX' },
  { code: '+63', name: 'Philippines', flag: '🇵🇭', format: 'XXX XXX XXXX' },
  { code: '+64', name: 'New Zealand', flag: '�🇳🇿', format: 'XX XXX XXXX' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬', format: 'XXXX XXXX' },
  { code: '+66', name: 'Thailand', flag: '🇹🇭', format: 'XX XXX XXXX' },
  { code: '+81', name: 'Japan', flag: '🇯🇵', format: 'XX XXXX XXXX' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷', format: 'XX XXXX XXXX' },
  { code: '+84', name: 'Vietnam', flag: '🇻🇳', format: 'XX XXX XXXX' },
  { code: '+86', name: 'China', flag: '🇨🇳', format: 'XXX XXXX XXXX' },
  { code: '+90', name: 'Turkey', flag: '🇹🇷', format: 'XXX XXX XXXX' },
  { code: '+91', name: 'India', flag: '🇮🇳', format: 'XXXXX XXXXX' },
  { code: '+92', name: 'Pakistan', flag: '🇵🇰', format: 'XXX XXX XXXX' },
  { code: '+93', name: 'Afghanistan', flag: '🇦🇫', format: 'XX XXX XXXX' },
  { code: '+94', name: 'Sri Lanka', flag: '🇱🇰', format: 'XX XXX XXXX' },
  { code: '+95', name: 'Myanmar', flag: '🇲🇲', format: 'XX XXX XXXX' },
  { code: '+98', name: 'Iran', flag: '🇮🇷', format: 'XXX XXX XXXX' },
  { code: '+212', name: 'Morocco', flag: '🇲🇦', format: 'XX XXX XXXX' },
  { code: '+213', name: 'Algeria', flag: '🇩🇿', format: 'XX XXX XXXX' },
  { code: '+216', name: 'Tunisia', flag: '🇹🇳', format: 'XX XXX XXX' },
  { code: '+218', name: 'Libya', flag: '🇱🇾', format: 'XX XXX XXXX' },
  { code: '+220', name: 'Gambia', flag: '🇬🇲', format: 'XXX XXXX' },
  { code: '+221', name: 'Senegal', flag: '🇸🇳', format: 'XX XXX XXXX' },
  { code: '+222', name: 'Mauritania', flag: '🇲🇷', format: 'XX XX XX XX' },
  { code: '+223', name: 'Mali', flag: '🇲🇱', format: 'XX XX XX XX' },
  { code: '+224', name: 'Guinea', flag: '🇬🇳', format: 'XX XXX XXXX' },
  { code: '+225', name: 'Ivory Coast', flag: '🇨🇮', format: 'XX XX XX XX' },
  { code: '+226', name: 'Burkina Faso', flag: '🇧🇫', format: 'XX XX XX XX' },
  { code: '+227', name: 'Niger', flag: '�🇪', format: 'XX XX XX XX' },
  { code: '+228', name: 'Togo', flag: '🇹🇬', format: 'XX XX XX XX' },
  { code: '+229', name: 'Benin', flag: '🇧🇯', format: 'XX XX XX XX' },
  { code: '+230', name: 'Mauritius', flag: '🇲🇺', format: 'XXXX XXXX' },
  { code: '+231', name: 'Liberia', flag: '🇱🇷', format: 'XX XXX XXXX' },
  { code: '+232', name: 'Sierra Leone', flag: '🇸🇱', format: 'XX XXXXXX' },
  { code: '+233', name: 'Ghana', flag: '🇬🇭', format: 'XX XXX XXXX' },
  { code: '+234', name: 'Nigeria', flag: '🇳🇬', format: 'XXX XXX XXXX' },
  { code: '+235', name: 'Chad', flag: '🇹🇩', format: 'XX XX XX XX' },
  { code: '+236', name: 'Central African Republic', flag: '��🇫', format: 'XX XX XX XX' },
  { code: '+237', name: 'Cameroon', flag: '🇨�', format: 'XX XX XX XX' },
  { code: '+238', name: 'Cape Verde', flag: '🇨🇻', format: 'XXX XXXX' },
  { code: '+239', name: 'São Tomé and Príncipe', flag: '🇸🇹', format: 'XX XXXXX' },
  { code: '+240', name: 'Equatorial Guinea', flag: '🇬🇶', format: 'XX XXX XXXX' },
  { code: '+241', name: 'Gabon', flag: '🇬🇦', format: 'X XX XX XX' },
  { code: '+242', name: 'Republic of the Congo', flag: '🇨🇬', format: 'XX XXX XXXX' },
  { code: '+243', name: 'Democratic Republic of the Congo', flag: '🇨🇩', format: 'XX XXX XXXX' },
  { code: '+244', name: 'Angola', flag: '🇦🇴', format: 'XXX XXX XXX' },
  { code: '+245', name: 'Guinea-Bissau', flag: '🇬🇼', format: 'XXX XXXX' },
  { code: '+246', name: 'British Indian Ocean Territory', flag: '🇮🇴', format: 'XXX XXXX' },
  { code: '+248', name: 'Seychelles', flag: '🇸🇨', format: 'X XXX XXX' },
  { code: '+249', name: 'Sudan', flag: '🇸🇩', format: 'XX XXX XXXX' },
  { code: '+250', name: 'Rwanda', flag: '🇷🇼', format: 'XXX XXX XXX' },
  { code: '+251', name: 'Ethiopia', flag: '🇪🇹', format: 'XX XXX XXXX' },
  { code: '+252', name: 'Somalia', flag: '🇸�', format: 'XX XXX XXXX' },
  { code: '+253', name: 'Djibouti', flag: '🇩🇯', format: 'XX XX XX XX' },
  { code: '+254', name: 'Kenya', flag: '🇰�', format: 'XXX XXXXXX' },
  { code: '+255', name: 'Tanzania', flag: '🇹🇿', format: 'XX XXX XXXX' },
  { code: '+256', name: 'Uganda', flag: '🇺🇬', format: 'XXX XXXXXX' },
  { code: '+257', name: 'Burundi', flag: '🇧🇮', format: 'XX XX XXXX' },
  { code: '+258', name: 'Mozambique', flag: '🇲🇿', format: 'XX XXX XXXX' },
  { code: '+260', name: 'Zambia', flag: '🇿🇲', format: 'XX XXX XXXX' },
  { code: '+261', name: 'Madagascar', flag: '🇲🇬', format: 'XX XX XXX XX' },
  { code: '+262', name: 'Réunion', flag: '🇷🇪', format: 'XXX XX XX XX' },
  { code: '+263', name: 'Zimbabwe', flag: '🇿🇼', format: 'XX XXX XXXX' },
  { code: '+264', name: 'Namibia', flag: '🇳�', format: 'XX XXX XXXX' },
  { code: '+265', name: 'Malawi', flag: '🇲🇼', format: 'XX XXX XXXX' },
  { code: '+266', name: 'Lesotho', flag: '🇱🇸', format: 'XX XXX XXXX' },
  { code: '+267', name: 'Botswana', flag: '�🇧�', format: 'XX XXX XXX' },
  { code: '+268', name: 'Eswatini', flag: '🇸🇿', format: 'XX XX XXXX' },
  { code: '+269', name: 'Comoros', flag: '🇰🇲', format: 'XXX XXXX' },
  { code: '+290', name: 'Saint Helena', flag: '🇸🇭', format: 'XXXX' },
  { code: '+291', name: 'Eritrea', flag: '🇪�🇷', format: 'X XXX XXX' },
  { code: '+297', name: 'Aruba', flag: '🇦🇼', format: 'XXX XXXX' },
  { code: '+298', name: 'Faroe Islands', flag: '🇫🇴', format: 'XXX XXX' },
  { code: '+299', name: 'Greenland', flag: '🇬🇱', format: 'XX XX XX' },
  { code: '+350', name: 'Gibraltar', flag: '🇬🇮', format: 'XXXX XXXX' },
  { code: '+351', name: 'Portugal', flag: '🇵🇹', format: 'XXX XXX XXX' },
  { code: '+352', name: 'Luxembourg', flag: '🇱🇺', format: 'XXX XXX XXX' },
  { code: '+353', name: 'Ireland', flag: '🇮🇪', format: 'XX XXX XXXX' },
  { code: '+354', name: 'Iceland', flag: '🇮🇸', format: 'XXX XXXX' },
  { code: '+355', name: 'Albania', flag: '🇦🇱', format: 'XX XXX XXXX' },
  { code: '+356', name: 'Malta', flag: '🇲🇹', format: 'XXXX XXXX' },
  { code: '+357', name: 'Cyprus', flag: '🇨🇾', format: 'XX XXX XXX' },
  { code: '+358', name: 'Finland', flag: '🇫🇮', format: 'XX XXX XXXX' },
  { code: '+359', name: 'Bulgaria', flag: '🇧🇬', format: 'XX XXX XXXX' },
  { code: '+370', name: 'Lithuania', flag: '🇱🇹', format: 'XXX XXXXX' },
  { code: '+371', name: 'Latvia', flag: '🇱🇻', format: 'XX XXX XXX' },
  { code: '+372', name: 'Estonia', flag: '🇪🇪', format: 'XXXX XXXX' },
  { code: '+373', name: 'Moldova', flag: '🇲🇩', format: 'XX XXX XXX' },
  { code: '+374', name: 'Armenia', flag: '🇦🇲', format: 'XX XXX XXX' },
  { code: '+375', name: 'Belarus', flag: '🇧🇾', format: 'XX XXX XX XX' },
  { code: '+376', name: 'Andorra', flag: '🇦🇩', format: 'XXX XXX' },
  { code: '+377', name: 'Monaco', flag: '🇲🇨', format: 'XX XX XX XX' },
  { code: '+378', name: 'San Marino', flag: '🇸🇲', format: 'XXXX XXXXXX' },
  { code: '+379', name: 'Vatican City', flag: '🇻🇦', format: 'XX XXXXXXXX' },
  { code: '+380', name: 'Ukraine', flag: '🇺🇦', format: 'XX XXX XX XX' },
  { code: '+381', name: 'Serbia', flag: '🇷🇸', format: 'XX XXX XXXX' },
  { code: '+382', name: 'Montenegro', flag: '🇲🇪', format: 'XX XXX XXX' },
  { code: '+383', name: 'Kosovo', flag: '🇽🇰', format: 'XX XXX XXX' },
  { code: '+385', name: 'Croatia', flag: '🇭🇷', format: 'XX XXX XXXX' },
  { code: '+386', name: 'Slovenia', flag: '🇸🇮', format: 'XX XXX XXX' },
  { code: '+387', name: 'Bosnia and Herzegovina', flag: '🇧🇦', format: 'XX XXX XXX' },
  { code: '+389', name: 'North Macedonia', flag: '🇲🇰', format: 'XX XXX XXX' },
  { code: '+420', name: 'Czech Republic', flag: '🇨🇿', format: 'XXX XXX XXX' },
  { code: '+421', name: 'Slovakia', flag: '🇸🇰', format: 'XXX XXX XXX' },
  { code: '+423', name: 'Liechtenstein', flag: '🇱🇮', format: 'XXX XXXX' },
  { code: '+500', name: 'Falkland Islands', flag: '🇫🇰', format: 'XXXXX' },
  { code: '+501', name: 'Belize', flag: '🇧🇿', format: 'XXX XXXX' },
  { code: '+502', name: 'Guatemala', flag: '🇬🇹', format: 'XXXX XXXX' },
  { code: '+503', name: 'El Salvador', flag: '🇸🇻', format: 'XXXX XXXX' },
  { code: '+504', name: 'Honduras', flag: '🇭🇳', format: 'XXXX XXXX' },
  { code: '+505', name: 'Nicaragua', flag: '🇳🇮', format: 'XXXX XXXX' },
  { code: '+506', name: 'Costa Rica', flag: '🇨🇷', format: 'XXXX XXXX' },
  { code: '+507', name: 'Panama', flag: '🇵🇦', format: 'XXXX XXXX' },
  { code: '+508', name: 'Saint Pierre and Miquelon', flag: '🇵🇲', format: 'XX XX XX' },
  { code: '+509', name: 'Haiti', flag: '🇭🇹', format: 'XX XX XXXX' },
  { code: '+590', name: 'Guadeloupe', flag: '🇬🇵', format: 'XXX XX XX XX' },
  { code: '+591', name: 'Bolivia', flag: '🇧🇴', format: 'X XXX XXXX' },
  { code: '+592', name: 'Guyana', flag: '🇬🇾', format: 'XXX XXXX' },
  { code: '+593', name: 'Ecuador', flag: '🇪�', format: 'XX XXX XXXX' },
  { code: '+594', name: 'French Guiana', flag: '🇬🇫', format: 'XXX XX XX XX' },
  { code: '+595', name: 'Paraguay', flag: '🇵🇾', format: 'XXX XXX XXX' },
  { code: '+596', name: 'Martinique', flag: '🇲🇶', format: 'XXX XX XX XX' },
  { code: '+597', name: 'Suriname', flag: '�🇸🇷', format: 'XXX XXXX' },
  { code: '+598', name: 'Uruguay', flag: '🇺🇾', format: 'X XXX XXXX' },
  { code: '+599', name: 'Curaçao', flag: '🇨🇼', format: 'X XXX XXXX' },
  { code: '+670', name: 'Timor-Leste', flag: '🇹🇱', format: 'XXX XXXX' },
  { code: '+672', name: 'Antarctica', flag: '🇦🇶', format: 'XXX XXX' },
  { code: '+673', name: 'Brunei', flag: '🇧🇳', format: 'XXX XXXX' },
  { code: '+674', name: 'Nauru', flag: '🇳🇷', format: 'XXX XXXX' },
  { code: '+675', name: 'Papua New Guinea', flag: '🇵🇬', format: 'XXX XXXX' },
  { code: '+676', name: 'Tonga', flag: '🇹🇴', format: 'XXXXX' },
  { code: '+677', name: 'Solomon Islands', flag: '🇸🇧', format: 'XXXXX' },
  { code: '+678', name: 'Vanuatu', flag: '🇻🇺', format: 'XXXXX' },
  { code: '+679', name: 'Fiji', flag: '🇫🇯', format: 'XXX XXXX' },
  { code: '+680', name: 'Palau', flag: '🇵🇼', format: 'XXX XXXX' },
  { code: '+681', name: 'Wallis and Futuna', flag: '🇼🇫', format: 'XX XX XX' },
  { code: '+682', name: 'Cook Islands', flag: '🇨🇰', format: 'XX XXX' },
  { code: '+683', name: 'Niue', flag: '🇳🇺', format: 'XXXX' },
  { code: '+685', name: 'Samoa', flag: '🇼🇸', format: 'XXXXX' },
  { code: '+686', name: 'Kiribati', flag: '🇰🇮', format: 'XXXX' },
  { code: '+687', name: 'New Caledonia', flag: '🇳🇨', format: 'XX XX XX' },
  { code: '+688', name: 'Tuvalu', flag: '🇹🇻', format: 'XXXXX' },
  { code: '+689', name: 'French Polynesia', flag: '��', format: 'XX XX XX' },
  { code: '+690', name: 'Tokelau', flag: '�🇹🇰', format: 'XXXX' },
  { code: '+691', name: 'Micronesia', flag: '🇫🇲', format: 'XXX XXXX' },
  { code: '+692', name: 'Marshall Islands', flag: '🇲🇭', format: 'XXX XXXX' },
  { code: '+850', name: 'North Korea', flag: '🇰�', format: 'XXX XXXX XXXX' },
  { code: '+852', name: 'Hong Kong', flag: '🇭🇰', format: 'XXXX XXXX' },
  { code: '+853', name: 'Macau', flag: '🇲🇴', format: 'XXXX XXXX' },
  { code: '+855', name: 'Cambodia', flag: '🇰🇭', format: 'XX XXX XXXX' },
  { code: '+856', name: 'Laos', flag: '🇱🇦', format: 'XX XX XXX XXX' },
  { code: '+880', name: 'Bangladesh', flag: '🇧🇩', format: 'XX XXX XXXX' },
  { code: '+886', name: 'Taiwan', flag: '🇹🇼', format: 'XXX XXX XXX' },
  { code: '+960', name: 'Maldives', flag: '🇲🇻', format: 'XXX XXXX' },
  { code: '+961', name: 'Lebanon', flag: '🇱🇧', format: 'XX XXX XXX' },
  { code: '+962', name: 'Jordan', flag: '🇯�', format: 'XX XXX XXXX' },
  { code: '+963', name: 'Syria', flag: '🇸�', format: 'XX XXX XXXX' },
  { code: '+964', name: 'Iraq', flag: '🇮🇶', format: 'XXX XXX XXXX' },
  { code: '+965', name: 'Kuwait', flag: '🇰🇼', format: 'XXXX XXXX' },
  { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦', format: 'XX XXX XXXX' },
  { code: '+967', name: 'Yemen', flag: '🇾🇪', format: 'XXX XXX XXX' },
  { code: '+968', name: 'Oman', flag: '🇴🇲', format: 'XXXX XXXX' },
  { code: '+970', name: 'Palestine', flag: '��', format: 'XX XXX XXXX' },
  { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪', format: 'XX XXX XXXX' },
  { code: '+972', name: 'Israel', flag: '🇮🇱', format: 'XX XXX XXXX' },
  { code: '+973', name: 'Bahrain', flag: '🇧🇭', format: 'XXXX XXXX' },
  { code: '+974', name: 'Qatar', flag: '🇶🇦', format: 'XXXX XXXX' },
  { code: '+975', name: 'Bhutan', flag: '🇧🇹', format: 'XX XXX XXX' },
  { code: '+976', name: 'Mongolia', flag: '🇲🇳', format: 'XX XX XXXX' },
  { code: '+977', name: 'Nepal', flag: '🇳🇵', format: 'XX XXX XXXX' },
  { code: '+992', name: 'Tajikistan', flag: '🇹🇯', format: 'XX XXX XXXX' },
  { code: '+993', name: 'Turkmenistan', flag: '🇹🇲', format: 'XX XXXXXX' },
  { code: '+994', name: 'Azerbaijan', flag: '🇦🇿', format: 'XX XXX XX XX' },
  { code: '+995', name: 'Georgia', flag: '🇬🇪', format: 'XXX XXX XXX' },
  { code: '+996', name: 'Kyrgyzstan', flag: '🇰🇬', format: 'XXX XXXXXX' },
  { code: '+998', name: 'Uzbekistan', flag: '🇺🇿', format: 'XX XXX XX XX' },
  { code: '+1242', name: 'Bahamas', flag: '🇧🇸', format: 'XXX XXXX' },
  { code: '+1246', name: 'Barbados', flag: '🇧🇧', format: 'XXX XXXX' },
  { code: '+1264', name: 'Anguilla', flag: '🇦🇮', format: 'XXX XXXX' },
  { code: '+1268', name: 'Antigua and Barbuda', flag: '🇦🇬', format: 'XXX XXXX' },
  { code: '+1284', name: 'British Virgin Islands', flag: '🇻🇬', format: 'XXX XXXX' },
  { code: '+1340', name: 'US Virgin Islands', flag: '🇻🇮', format: 'XXX XXXX' },
  { code: '+1345', name: 'Cayman Islands', flag: '🇰🇾', format: 'XXX XXXX' },
  { code: '+1441', name: 'Bermuda', flag: '🇧🇲', format: 'XXX XXXX' },
  { code: '+1473', name: 'Grenada', flag: '🇬🇩', format: 'XXX XXXX' },
  { code: '+1649', name: 'Turks and Caicos Islands', flag: '🇹🇨', format: 'XXX XXXX' },
  { code: '+1664', name: 'Montserrat', flag: '🇲🇸', format: 'XXX XXXX' },
  { code: '+1670', name: 'Northern Mariana Islands', flag: '🇲🇵', format: 'XXX XXXX' },
  { code: '+1671', name: 'Guam', flag: '🇬🇺', format: 'XXX XXXX' },
  { code: '+1684', name: 'American Samoa', flag: '🇦🇸', format: 'XXX XXXX' },
  { code: '+1721', name: 'Sint Maarten', flag: '🇸🇽', format: 'XXX XXXX' },
  { code: '+1758', name: 'Saint Lucia', flag: '🇱🇨', format: 'XXX XXXX' },
  { code: '+1767', name: 'Dominica', flag: '🇩�', format: 'XXX XXXX' },
  { code: '+1784', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', format: 'XXX XXXX' },
  { code: '+1809', name: 'Dominican Republic', flag: '🇩🇴', format: 'XXX XXXX' },
  { code: '+1868', name: 'Trinidad and Tobago', flag: '🇹🇹', format: 'XXX XXXX' },
  { code: '+1869', name: 'Saint Kitts and Nevis', flag: '🇰�', format: 'XXX XXXX' },
  { code: '+1876', name: 'Jamaica', flag: '🇯🇲', format: 'XXX XXXX' },
];

// Get the current country info
const currentCountry = computed(() => {
  return countries.find(c => c.code === selectedCountry.value) || countries[0];
});

// Emit the full E.164 formatted number
const updatePhoneNumber = () => {
  // Remove all non-digit characters
  const cleanNumber = phoneNumber.value.replace(/\D/g, '');
  
  if (cleanNumber) {
    // Emit in E.164 format: +[country code][number]
    emit('update:modelValue', `${selectedCountry.value}${cleanNumber}`);
  } else {
    emit('update:modelValue', '');
  }
};

// Format the phone number for display
const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, '');
  
  // Apply country-specific formatting
  if (selectedCountry.value === '+1' && cleaned.length >= 10) {
    // US/Canada format: (XXX) XXX-XXXX
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (selectedCountry.value === '+44' && cleaned.length >= 10) {
    // UK format: XXXX XXX XXXX
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 11)}`;
  }
  
  // Default: add spaces every 3-4 digits
  return cleaned.replace(/(\d{3})(?=\d)/g, '$1 ');
};

// Handle input
const handleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const cursorPosition = target.selectionStart || 0;
  const oldLength = phoneNumber.value.length;
  
  // Get raw digits only
  const rawValue = target.value.replace(/\D/g, '');
  
  // Update the model
  phoneNumber.value = rawValue;
  
  // Format for display
  const formatted = formatPhoneNumber(rawValue);
  target.value = formatted;
  
  // Restore cursor position (accounting for added formatting)
  const newLength = formatted.length;
  const diff = newLength - oldLength;
  const newPosition = cursorPosition + diff;
  
  target.setSelectionRange(newPosition, newPosition);
  
  // Emit the change
  updatePhoneNumber();
};
</script>

<template>
  <div class="space-y-2">
    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
      Phone Number
    </label>
    
    <div class="flex gap-2">
      <!-- Country Code Selector -->
      <select
        v-model="selectedCountry"
        @change="updatePhoneNumber"
        :disabled="disabled"
        class="w-32 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-white"
      >
        <option v-for="country in countries" :key="country.code" :value="country.code">
          {{ country.flag }} {{ country.code }}
        </option>
      </select>

      <!-- Phone Number Input -->
      <div class="flex-1 relative">
        <input
          type="tel"
          :value="formatPhoneNumber(phoneNumber)"
          @input="handleInput"
          :disabled="disabled"
          :placeholder="currentCountry.format"
          class="w-full px-4 py-2 bg-white dark:bg-gray-800 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-white"
          :class="error ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'"
        />
        
        <!-- Format hint -->
        <div class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
          {{ currentCountry.name }}
        </div>
      </div>
    </div>

    <!-- Error message -->
      <p v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400">
        {{ error }}
        <span v-if="error.includes('blocked') || error.includes('not supported')" class="block mt-2 text-xs">
          This number may be blocked due to too many verification attempts. Try a different number or contact support.
        </span>
      </p>

    <!-- Help text -->
    <p v-else class="text-xs text-gray-500 dark:text-gray-400">
      We'll send a verification code to this number
    </p>
  </div>
</template>

<style scoped>
/* Remove number input arrows */
input[type='tel']::-webkit-inner-spin-button,
input[type='tel']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='tel'] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
